/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import { Diagnostics } from '@tivocorp/tivo-webapp-loggers';

import viewAllImage from '../../../assets/images/hydra_icon_view_all_list.png';
import { APPLICATION_FEATURE_AREA } from '../../../config';
import { BrandingUiInfoKeys } from '../../../core/entities';
import { NdvrInteractor } from '../../../core/interactors';
import { Locale, LocaleKeys } from '../../../core/locale';
import { Log } from '../../../core/logging';
import { TUpcomingOffer, TUpcomingOffersList } from '../../../core/types';
import { DateTime, UniqueIdentifier } from '../../../core/utils';
import { ProgramScreenConstants } from '../../../store/programscreen/ProgramScreenConstants';
import {
    THorizontalActionStripImageItem,
    THorizontalActionStripLayout,
} from './THorizontalActionStripItem';

export class BiaxialActionWidgetHelper {
    private static id: string;

    private static totalCount: number | null;

    private static limit: 4 | 8;

    // determines highlighted image
    private static currentImageIndex: number;

    // no of tiles before the highlighted image
    private static precedingTileCount: 1 | 2;

    private static contentType: string;

    private static layout: Extract<
        THorizontalActionStripLayout,
        ProgramScreenConstants.UPCOMING_AIRING | ProgramScreenConstants.ALL_UPCOMING_EPISODES
    >;

    private static services = new NdvrInteractor();

    public static init(
        id: string,
        contentType: string,
        layout: Extract<
            THorizontalActionStripLayout,
            ProgramScreenConstants.UPCOMING_AIRING | ProgramScreenConstants.ALL_UPCOMING_EPISODES
        >,
    ) {
        this.id = id;
        this.totalCount = null;
        this.limit = contentType.includes('movie') ? 8 : 4;
        this.precedingTileCount = 1;
        this.contentType = contentType;
        this.currentImageIndex = 0;
        this.layout = layout;
    }

    public static getId(): string {
        return this.id;
    }

    public static getTotalCount(): number | null {
        return this.totalCount;
    }

    public static getLimit(): number {
        return this.limit;
    }

    public static getCurrentImageIndex(): number {
        return this.currentImageIndex;
    }

    public static getNextOffset(direction: 'left' | 'right'): number {
        return direction === 'left'
            ? (this.currentImageIndex - 1 + this.totalCount!) % this.totalCount!
            : (this.currentImageIndex + 1) % this.totalCount!;
    }

    private static getSafeLimitForOffset(offset: number, limit: number): number {
        // -1 as View All is superficial
        return this.totalCount !== null ? Math.min(this.totalCount - offset - 1, limit) : limit;
    }

    // Helper function to return an empty strip image when no offers are available
    private static fetchEmptyTile(): THorizontalActionStripImageItem {
        return {
            type: 'PosterImage',
            displayDetails: {
                name: Locale.getString(LocaleKeys.VIEW_ALL_TEXT),
                image: this.services.getAppUiImageByKey(
                    BrandingUiInfoKeys.APP_ICONS.DEFAULT_EMPTY_STRIP,
                )?.imageUrl,
                layout: this.layout,
                identifier: ProgramScreenConstants.EMPTY_STRIP,
                title: 'Empty strip',
            },
        };
    }

    private static getLoadingTile(): THorizontalActionStripImageItem {
        return {
            type: this.contentType === 'movie' ? 'LandscapeImage' : 'PosterImage',
            displayDetails: {
                name: 'Loading',
                layout: this.layout,
                identifier: ProgramScreenConstants.LOADING_STRIP,
            },
        };
    }

    private static getViewAllTile(): THorizontalActionStripImageItem {
        return {
            type: 'PosterImage',
            displayDetails: {
                identifier: ProgramScreenConstants.VIEW_ALL,
                name: Locale.getString(LocaleKeys.VIEW_ALL_TEXT),
                image: viewAllImage,
                layout: this.layout,
                title: 'All episodes',
            },
        };
    }

    // Helper function to map the upcoming offers to the required image info format
    private static mapUpcomingOffersToImageInfo(
        upcomingOffers: TUpcomingOffer[],
    ): THorizontalActionStripImageItem[] {
        return upcomingOffers.map((data) => ({
            type: data.contentType === 'movie' ? 'LandscapeImage' : 'PosterImage',
            displayDetails: {
                name: 'All episodes',
                image: data.imageUrl,
                isLive: data.isLive,
                isNew: data.isNew,
                identifier: ProgramScreenConstants.UPCOMING_AIRING,
                channelUrl: data.channelUrl,
                episodeNumber: data.episodeNumber,
                seasonNumber: data.seasonNumber,
                layout: this.layout,
                title: data.title,
                offerId: data.offerId,
                contentType: data.contentType,
                originalAirDate: new DateTime(data.originalAirDate).strftime('%M/%d/%Y'),
            },
        }));
    }

    public static async fetchData(
        offset: number,
        isInit = false,
    ): Promise<{
        imageInfo: THorizontalActionStripImageItem[];
        totalCount: number;
        networkCallId: number;
    }> {
        const minEndTime = new Date().toISOString();
        // Next upcoming network call ID
        const networkCallId = UniqueIdentifier.getNextUpcomingNetworkCallId();
        this.currentImageIndex = offset; // Update offset

        try {
            let data: TUpcomingOffer[] = [];
            let itemsFetched = 0;
            let res: TUpcomingOffersList = { upcomingOffers: [], totalCount: 0, responseToken: '' };
            let safeLimit = this.getSafeLimitForOffset(offset, this.limit);

            if (safeLimit !== 0) {
                res = await this.services.getUpcomingOffersForCollectionId({
                    objectId: this.id,
                    minEndTime,
                    offset,
                    limit: safeLimit,
                    screenType: APPLICATION_FEATURE_AREA.PROGRAM_SCREEN,
                });
            }

            if (isInit) {
                this.totalCount = res.totalCount > 0 ? res.totalCount + 1 : 0;
                this.precedingTileCount =
                    this.contentType.includes('movie') && this.totalCount >= 8 ? 2 : 1;
            }
            const tCount = this.totalCount!;

            if (tCount === 0)
                return { imageInfo: [this.fetchEmptyTile()], totalCount: tCount, networkCallId };

            data = res.upcomingOffers.slice(
                0,
                Math.min(this.limit, tCount) - this.precedingTileCount,
            );
            const leftCornerOffset = offset - this.precedingTileCount;
            const rightCornerOffset =
                offset + (Math.min(this.limit, tCount) - (this.precedingTileCount + 1));
            const shouldIncludeViewAllTile =
                leftCornerOffset < 0 || rightCornerOffset >= tCount - 1;
            itemsFetched = data.length + (shouldIncludeViewAllTile ? 1 : 0);

            /**
             * Eg.Case (Right Slice) : Visible List [6, 7(highlight), 8, 0], offset = 7
             * 1st call gets [7]  ([8(View all)] -> will injected later,
             *                      but it is counted as fetched)
             * This is required to get [0]
             * Other eg. [1, 2(highlight, viewall), 0]
             */
            if (this.limit > itemsFetched && itemsFetched < tCount && rightCornerOffset >= tCount) {
                safeLimit = this.getSafeLimitForOffset(
                    0,
                    Math.min(this.limit, tCount) - itemsFetched - this.precedingTileCount,
                );
                if (safeLimit !== 0) {
                    res = await this.services.getUpcomingOffersForCollectionId({
                        objectId: this.id,
                        minEndTime,
                        offset: 0,
                        limit: safeLimit,
                        screenType: APPLICATION_FEATURE_AREA.PROGRAM_SCREEN,
                    });
                    itemsFetched += res.upcomingOffers.length;
                }
                data = [...data, ...res.upcomingOffers];
            }

            /**
             * Eg.Case (Left Slice) : Visible List [6, 7(highlight), 8(View all), 0] , offset = 7
             * 1st call gets [7]  ([8(View all)] -> will injected later,
             *                      but it is counted as fetched)
             * 2nd call gets [0]
             * And this is required to get [6]
             * Other eg. [1, 2(highlight, view all), 0]
             */
            if (this.limit > itemsFetched && itemsFetched < tCount) {
                let newOffset = (offset - this.precedingTileCount + tCount) % tCount;
                let newLimit = this.precedingTileCount;
                // Case [8(View all), 0, 1, 2, 3, 4, 5, 6] offset = 1
                if (this.precedingTileCount === 2 && newOffset === tCount - 1) {
                    newOffset = 0;
                    newLimit = 1;
                }
                safeLimit = this.getSafeLimitForOffset(newOffset, newLimit);
                if (safeLimit !== 0) {
                    res = await this.services.getUpcomingOffersForCollectionId({
                        objectId: this.id,
                        minEndTime,
                        offset: newOffset,
                        limit: safeLimit,
                        screenType: APPLICATION_FEATURE_AREA.PROGRAM_SCREEN,
                    });
                    data = [...res.upcomingOffers, ...data];
                    itemsFetched += res.upcomingOffers.length;
                }
            }

            const imageInfo = this.mapUpcomingOffersToImageInfo(data);
            if (shouldIncludeViewAllTile) {
                const idx =
                    offset < this.precedingTileCount
                        ? this.precedingTileCount - offset - 1
                        : tCount - 1 - offset + this.precedingTileCount;
                imageInfo.splice(idx, 0, this.getViewAllTile());
            }

            return { imageInfo, totalCount: tCount, networkCallId };
        } catch (error: any) {
            Log.e('BiaxialActionWidgetHelper', error.message);
            Diagnostics.error('BiaxialActionWidgetHelper', error.message);
            return { imageInfo: [this.fetchEmptyTile()], totalCount: 0, networkCallId };
        }
    }

    public static getLoadingState(
        curOffset: number,
        nextOffset: number,
        dir: 'left' | 'right',
        curState: THorizontalActionStripImageItem[],
    ): THorizontalActionStripImageItem[] {
        if (curOffset === nextOffset) return curState;

        const tCount = this.totalCount!;
        const loadingState = new Array(this.limit).fill(this.getLoadingTile());

        const nextCornerOffset =
            dir === 'right'
                ? (nextOffset - this.precedingTileCount + tCount) % tCount
                : (nextOffset + this.limit - this.precedingTileCount - 1) % tCount;
        let match = -1;
        // back part of the list
        for (let i = 0; i < this.precedingTileCount; i += 1)
            if ((curOffset - this.precedingTileCount + i + tCount) % tCount === nextCornerOffset) {
                match = i;
                break;
            }
        if (match === -1)
            // front part of the list including current
            for (let i = 0; i < curState.length - this.precedingTileCount; i += 1)
                if ((curOffset + i) % tCount === nextCornerOffset) {
                    match = i + this.precedingTileCount;
                    break;
                }

        if (match === -1) return loadingState;

        if (dir === 'right')
            for (let i = match; i < curState.length; i += 1) loadingState[i - match] = curState[i];
        else
            for (let i = 0; i <= match; i += 1)
                loadingState[i + (curState.length - 1 - match)] = curState[i];

        return loadingState;
    }

    public static reset() {
        this.services.clearUpcomingOfferCache();
        this.totalCount = null;
        this.currentImageIndex = 0;
        this.contentType = '';
    }
}
